import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Gift, MapPin, Phone } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { useBrandFont } from "../lib/brandFonts";

export default function FidelityCard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useBrandFont(data?.font || "");

  useEffect(() => {
    api.get(`/public/fidelity/${token}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6 text-center">
      <p className="text-zinc-400 text-sm">{error}</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <p className="text-zinc-500 text-sm">Caricamento...</p>
    </div>
  );

  const next = data.next_premio;
  const progress = next ? Math.min(100, Math.round((data.punti / next.punti) * 100)) : 100;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const colore = data.livello?.colore || "#18181b";

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center px-4 py-8" data-testid="fidelity-card-page" data-brand-font="">
      <div className="w-full max-w-sm space-y-4">
        {/* Card */}
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-zinc-800"
          style={{ background: `linear-gradient(135deg, ${colore} 0%, #09090b 150%)` }} data-testid="fidelity-card">
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              {data.logo_url ? (
                <img src={data.logo_url} alt={data.restaurant_name}
                  className="h-11 w-11 rounded-lg object-cover bg-white" />
              ) : (
                <div className="h-11 w-11 rounded-lg bg-amber-400 flex items-center justify-center">
                  <span className="font-display font-black text-zinc-900 text-lg">
                    {data.restaurant_name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display font-bold text-white truncate">{data.restaurant_name}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400">
                  {data.nome_programma || "Fidelity Card"}
                </div>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Titolare</div>
              <div className="text-white font-semibold" data-testid="card-customer-name">{data.customer_name}</div>
              {data.livello?.nome && (
                <span className="inline-block mt-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/30 text-white"
                  data-testid="card-livello">{data.livello.nome}</span>
              )}
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Saldo punti</div>
                <div className="font-display text-5xl font-black text-amber-400" data-testid="card-saldo">
                  {data.punti}
                </div>
              </div>
              <img src={`${apiBase}/api/public/fidelity/${token}/qr.png`} alt="QR fidelity"
                className="w-20 h-20 rounded-lg bg-white p-1" data-testid="card-qr" />
            </div>
          </div>
          {next && (
            <div className="px-6 pb-5">
              <div className="flex justify-between text-[11px] text-zinc-400 mb-1.5">
                <span>Prossimo premio: <strong className="text-zinc-200">{next.premio}</strong></span>
                <span>{data.punti}/{next.punti}</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${progress}%` }}
                  data-testid="card-progress" />
              </div>
            </div>
          )}
        </div>

        {/* Premi */}
        {data.premi.length > 0 && (
          <div className="rounded-xl bg-zinc-900 border border-zinc-800 divide-y divide-zinc-800" data-testid="card-premi">
            <div className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Premi</div>
            {data.premi.map((p, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`card-premio-${i}`}>
                <Gift size={18} weight="bold" className={p.sbloccato ? "text-amber-400" : "text-zinc-600"} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${p.sbloccato ? "text-white" : "text-zinc-400"}`}>{p.premio}</div>
                </div>
                <div className={`text-xs font-black ${p.sbloccato ? "text-emerald-400" : "text-zinc-500"}`}>
                  {p.sbloccato ? "SBLOCCATO" : `${p.punti} pt`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contatti */}
        <div className="rounded-xl bg-zinc-900 border border-zinc-800 px-4 py-3 space-y-1.5">
          {data.indirizzo && (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <MapPin size={14} className="text-zinc-500" /> {data.indirizzo}
            </div>
          )}
          {data.telefono && (
            <a href={`tel:${data.telefono}`} className="flex items-center gap-2 text-xs text-zinc-400">
              <Phone size={14} className="text-zinc-500" /> {data.telefono}
            </a>
          )}
        </div>

        <p className="text-center text-[10px] text-zinc-600 pt-2">
          Mostra il QR in cassa per accumulare e riscattare i punti · Powered by Dishenda
        </p>
      </div>
    </div>
  );
}
